import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import DataStore from '@leverege/data-store/lib/web/browser.js';
import I18N from '@leverege/i18n';
import { DataSources } from '@leverege/ui-attributes';
import { Flex, Text, Toast } from '@leverege/ui-elements';
import { Toolbar } from '@leverege/ui-plugin';
import { GlobalState } from '@leverege/ui-redux';
import { isTagInGatewayRange, isTagRingCommandSent, isTagRinging, isTagWaitingForDetection } from "./Util.js";
import C from "./Constants.js";
import S from './FindAssetDetailRenderer.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t
} = I18N.ns('recovr.details.findTag.FindTagDetailRenderer');
const EMPTY = {};
const client = 'DataCardDetail';
export default class FindTagDetailRenderer {
  constructor(model) {
    this.model = model;
  }
  getValueComponent(object, cxt, ...rest) {
    const {
      type
    } = object;
    const context = {
      ...cxt,
      client,
      clientProps: {
        ...cxt.clientProps,
        settings: {
          type: 'findTag'
        }
      }
    };
    const matchContext = {
      use: 'actionBar',
      objectType: type,
      client
    };
    return /*#__PURE__*/_jsx(ManagedValueComponent, {
      tag: object?.id,
      tagObjRef: object,
      context: context,
      matchContext: matchContext,
      model: this.model
    });
  }
  getComponents(object, cxt = EMPTY, ...rest) {
    return [{
      style: {
        padding: '0px'
      },
      component: this.getValueComponent(object, cxt)
    }];
  }
}
function TagInGatewayRange({
  tagId,
  context,
  matchContext,
  model
}) {
  return /*#__PURE__*/_jsxs(Flex, {
    className: S.row,
    style: model.style,
    align: "center",
    justify: "space-between",
    variant: "pairDetail",
    children: [/*#__PURE__*/_jsxs(Flex, {
      variant: "colM",
      className: S.label,
      children: [/*#__PURE__*/_jsx(Text, {
        className: S.title,
        children: t('tagInGatewayRangeTitle', {
          tagId
        })
      }), /*#__PURE__*/_jsx(Text, {
        className: S.subtitle,
        children: t('tagInGatewayRangeSubtitle')
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: S.value,
      children: /*#__PURE__*/_jsx(Toolbar, {
        variant: "secondary",
        context: context,
        matchContext: matchContext
      })
    })]
  });
}
function TagOutOfGatewayRange({
  context,
  matchContext,
  model
}) {
  return /*#__PURE__*/_jsxs(Flex, {
    className: S.row,
    style: model.style,
    align: "center",
    justify: "space-between",
    variant: "pairDetail",
    children: [/*#__PURE__*/_jsxs(Flex, {
      variant: "colM",
      children: [/*#__PURE__*/_jsx(Text, {
        className: S.title,
        children: t('tagOutOfGatewayRangeTitle')
      }), /*#__PURE__*/_jsx(Text, {
        className: S.subtitle,
        children: t('tagOutOfGatewayRangeSubtitle')
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: S.value,
      children: /*#__PURE__*/_jsx(Toolbar, {
        context: context,
        matchContext: matchContext
      })
    })]
  });
}
function TagConnecting({
  tagId,
  context,
  matchContext,
  model
}) {
  return /*#__PURE__*/_jsxs(Flex, {
    className: S.row,
    style: model.style,
    align: "center",
    justify: "space-between",
    variant: "pairDetail",
    children: [/*#__PURE__*/_jsxs(Flex, {
      variant: "colM",
      children: [/*#__PURE__*/_jsx(Text, {
        className: S.title,
        children: t('tagConnectingTitle', {
          tagId
        })
      }), /*#__PURE__*/_jsx(Text, {
        className: S.subtitle,
        children: t('tagConnectingSubtitle')
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: S.value,
      children: /*#__PURE__*/_jsx(Toolbar, {
        variant: "primary",
        context: context,
        matchContext: matchContext
      })
    })]
  });
}
function TagRequestSent({
  tagId,
  context,
  matchContext,
  model
}) {
  return /*#__PURE__*/_jsxs(Flex, {
    className: S.row,
    style: model.style,
    align: "center",
    justify: "space-between",
    variant: "pairDetail",
    children: [/*#__PURE__*/_jsx(Flex, {
      variant: "colM",
      children: /*#__PURE__*/_jsx(Text, {
        className: S.title,
        children: t('tagRequestSentTitle', {
          tagId
        })
      })
    }), /*#__PURE__*/_jsx("div", {
      className: S.value,
      children: /*#__PURE__*/_jsx(Toolbar, {
        context: context,
        matchContext: matchContext
      })
    })]
  });
}
function TagRinging({
  tagId,
  context,
  matchContext,
  model
}) {
  return /*#__PURE__*/_jsxs(Flex, {
    className: S.row,
    style: model.style,
    align: "center",
    justify: "space-between",
    variant: "pairDetail",
    children: [/*#__PURE__*/_jsx(Flex, {
      variant: "colM",
      children: /*#__PURE__*/_jsx(Text, {
        className: S.title,
        children: t('tagRingingTitle', {
          tagId
        })
      })
    }), /*#__PURE__*/_jsx("div", {
      className: S.value,
      children: /*#__PURE__*/_jsx(Toolbar, {
        context: context,
        matchContext: matchContext
      })
    })]
  });
}
function ManagedValueComponent({
  tagObjRef,
  context,
  matchContext,
  model
}) {
  const delegate = DataSources.getDelegate(tagObjRef);
  const tag = useSelector(state => delegate.getItem(state));
  const prevTag = useRef(tag);
  const tagId = tag?.name || tag?.data?.name;
  const dataStore = useRef();
  const dataStoreTimeout = useRef();
  const onData = async snapshot => {
    // const data = snapshot.val()

    // Waiting for es propagation. delegate.get() pulls from es.
    await new Promise(resolve => {
      setTimeout(resolve, 2000);
    });

    // Instantiate key item delegate.
    const delegate = DataSources.getDelegate(tagObjRef);

    // Only way to get the custom renderer to re-render programatically.
    await GlobalState.dispatch(delegate.get()).then(tag => {
      // If the key ring command state changes from something else to 'failed', display error toast.
      if (tag?.data?.commands?.ring?.state === C.TAG_RING_COMMAND_STATES.FAILED && tag?.data?.commands?.ring?.state !== C.TAG_RING_COMMAND_STATES.FAILED) {
        Toast.error(t('commandFailed'));
      }

      // If the key ring command state changes from 'waitingForDetection' to empty, display error toast.
      if (tag?.data?.commands?.ring?.state == null && tag?.data?.commands?.ring?.state === C.TAG_RING_COMMAND_STATES.WAITING_FOR_DETECTION) {
        Toast.error(t('commandNotSent'));
      }
      prevTag.current = tag;
    });
  };
  const stopListener = () => {
    if (dataStore.current) {
      clearTimeout(dataStoreTimeout.current);
      dataStore.current.off('', onData);
    }
  };
  const startListener = async () => {
    if (!dataStore.current) {
      // Instantiate key item delegate.
      const delegate = DataSources.getDelegate(tagObjRef);

      // Get imagine device from firebase.
      const firebaseImagineDevice = await GlobalState.dispatch(delegate.get({
        blueprint: true,
        accessToken: true
      }));

      // Listen for device updates.
      dataStore.current = DataStore.createDatabase(firebaseImagineDevice.accessToken);
      await dataStore.current.signIn(firebaseImagineDevice.accessToken.jwt);
      dataStore.current.on('', onData);

      // Turn off listener after 2 minutes.
      clearTimeout(dataStoreTimeout.current);
      dataStoreTimeout.current = setTimeout(stopListener, C.WAITING_FOR_DETECTION_MAX_DURATION_MS + C.WAITING_FOR_RESPONSE_MAX_DURATION_MS);
    }
  };
  const onCommand = async () => {
    // Turn on listener.
    startListener();

    // Turn off listener after 2 minutes.
    dataStoreTimeout.current = setTimeout(stopListener, C.WAITING_FOR_DETECTION_MAX_DURATION_MS + C.WAITING_FOR_RESPONSE_MAX_DURATION_MS);
  };
  useEffect(() => {
    if (tag?.data?.commands?.ring?.state) {
      // Turn on listener on mount.
      startListener();
    }

    // Turn off listener on unmount.
    return stopListener;
  }, [tag]);
  if (isTagWaitingForDetection(tag)) {
    return /*#__PURE__*/_jsx(TagConnecting, {
      tagId: tagId,
      context: {
        ...context,
        onCommand
      },
      matchContext: matchContext,
      model: model
    });
  }
  if (isTagRingCommandSent(tag)) {
    return /*#__PURE__*/_jsx(TagRequestSent, {
      tagId: tagId,
      context: {
        ...context,
        onCommand
      },
      matchContext: matchContext,
      model: model
    });
  }

  // Ringing lasts for 30 seconds. 5 seconds buffer was requested by product/design team because reasons.
  if (isTagRinging(tag)) {
    return /*#__PURE__*/_jsx(TagRinging, {
      tagId: tagId,
      context: {
        ...context,
        onCommand
      },
      matchContext: matchContext,
      model: model
    });
  }
  if (isTagInGatewayRange(tag)) {
    return /*#__PURE__*/_jsx(TagInGatewayRange, {
      tagId: tagId,
      context: {
        ...context,
        onCommand
      },
      matchContext: matchContext,
      model: model
    });
  }
  return /*#__PURE__*/_jsx(TagOutOfGatewayRange, {
    context: {
      ...context,
      onCommand
    },
    matchContext: matchContext,
    model: model
  });
}